// Project, the ChatShell sidebar section and its dialogs (plan F3; design
// review ui.region_gate projects: divided section, relevance 2.97).
//
//   ProjectsProvider  owns the list (GET /projects), the Buat project and
//                     Ganti nama project dialog, and the Hapus confirmation,
//                     so the sidebar and the project page (screens/Project.tsx)
//                     share one list and one set of dialogs.
//   ProjectsSection   the sidebar: the "Project" heading, the Project baru row
//                     (opens Buat project), then one row per project (folder
//                     glyph, the name as a link to /project/:id, a moreH Menu
//                     with Ganti nama and Hapus), the 5 newest only (plus the
//                     open one when it is older). The open project (its home
//                     or one of its conversations, /project/:id/c/:conv) is
//                     the current row, with its latest 3 conversations as
//                     indented link rows under it. "Lihat semua" comes last:
//                     the PBJ Project page (/project-pbj) for a Non-Penyedia,
//                     Project Penyedia (/project-penyedia) for a Penyedia.
//
// A Penyedia's Project baru opens penyedia/PaketCreateDialog.tsx: the name
// optional (default "Project baru"), the LPSE announcement required; Pluto
// reads it into the paket info, which the user checks before it is saved.
//
// A Non-Penyedia's Buat project also asks the kind: Paket PBJ (the
// default; the route with its method or Swakelola type and the paket data,
// pbj/PbjCreateFields)
// or a plain Folder. A Paket PBJ is created through POST /pbj/projects and
// opens the PBJ home (pbj/PbjProject.tsx); its row shows the building glyph.
//
// Buat project: the name field with a leading folder glyph, one tonal
// sentence on what a project is (relevance 1.85), and Buat project, enabled
// once the trimmed name is not empty. Enter submits, Escape closes, focus
// starts in the field. A new project lands at the top of the list and the
// app opens it. Errors (the 100-project limit, a name the API refuses, the
// network) are named inside the dialog.
import { createContext, Fragment, useCallback, useContext, useEffect, useId, useMemo, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import * as API from "../api";
import type { Project } from "../api";
import { useMaybeUser } from "../auth";
import { navigate, useLocation } from "../router";
import { projectConversationPath, useProjectConversations } from "../project/ProjectChats";
import { Dialog, Field, Notice, useAsync, type Async } from "../ui/components";
import { Glyph } from "../ui/icons";
import { SectionHead } from "./SectionHead";
import { SideRow } from "./SideRow";
import { NAV_PROJECTS_KEY, useFlag } from "../lib/prefs";
import { PBJ_PROJECTS_PATH, PENYEDIA_PROJECTS_PATH } from "./nav";
import { isNonPenyedia } from "../lib/labels";
import { DEFAULT_PBJ_CHOICE, PbjCreateFields, type PbjChoice } from "../pbj/PbjCreateFields";
import { paketInput, tahunError } from "../pbj/PaketFields";
import { PaketCreateDialog } from "../penyedia/PaketCreateDialog";

/** API limit: a project name is 1 to 120 characters. */
export const PROJECT_NAME_MAX = 120;

/** The path of one project's page. */
export function projectPath(id: string): string {
  return `/project/${encodeURIComponent(id)}`;
}

/** How many of the open project's conversations the sidebar lists under it. */
export const SIDEBAR_PROJECT_CHATS = 3;

/** How many projects the sidebar section lists (the newest first). */
export const SIDEBAR_PROJECTS = 5;

/** The project (and conversation) open in main: /project/:id or /project/:id/c/:conv. */
export function projectRouteFrom(path: string): { projectId: string; conversationId: string | null } | null {
  const m = /^\/project\/([^/]+)(?:\/c\/([^/]+))?\/?$/.exec(path);
  if (!m) return null;
  try {
    return { projectId: decodeURIComponent(m[1]!), conversationId: m[2] ? decodeURIComponent(m[2]) : null };
  } catch {
    return null;
  }
}

/** The shell's id, else the one in the path (the shell passes none for a conversation inside a project). */
function useOpenProject(currentId: string | null): { projectId: string | null; conversationId: string | null } {
  const route = projectRouteFrom(useLocation().path);
  return { projectId: currentId ?? route?.projectId ?? null, conversationId: route?.conversationId ?? null };
}

function projectError(err: unknown): string {
  if (err instanceof API.ApiError && err.status === 409) {
    if (err.body?.code === "project_limit") return "Batas 100 project sudah tercapai. Hapus project yang tidak dipakai, lalu coba lagi.";
    return "Nama project ini sudah dipakai. Pilih nama lain.";
  }
  return API.errorMessage(err);
}

function cleanName(raw: string): string {
  return raw.replace(/\s+/g, " ").trim().slice(0, PROJECT_NAME_MAX);
}

type DialogState = { kind: "create" } | { kind: "rename"; project: Project } | { kind: "delete"; project: Project } | null;

export interface ProjectsApi {
  list: Async<Project[]>;
  openCreate: () => void;
  openRename: (p: Project) => void;
  openDelete: (p: Project) => void;
}

const ProjectsContext = createContext<ProjectsApi | null>(null);

/** The shared project list and dialogs, or null outside a signed-in shell with the projects feature. */
export function useProjects(): ProjectsApi | null {
  return useContext(ProjectsContext);
}

export interface ProjectsProviderProps {
  /** Signed in with the projects feature on; otherwise nothing is fetched. */
  enabled: boolean;
  /** The project open in main (/project/:id), or null. */
  currentId: string | null;
  children: ReactNode;
}

export function ProjectsProvider({ enabled, currentId: shellId, children }: ProjectsProviderProps) {
  const currentId = useOpenProject(shellId).projectId;
  const role = useMaybeUser()?.role;
  const penyedia = role === "penyedia";
  const nonPenyedia = !!role && isNonPenyedia(role);
  const list = useAsync<Project[]>(() => (enabled ? API.listProjects() : Promise.resolve([])), [enabled]);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = useCallback((next: DialogState) => {
    setError(null);
    setDialog(next);
  }, []);

  const close = () => {
    if (busy) return;
    setDialog(null);
    setError(null);
  };

  const rows = list.data ?? [];

  const save = async (name: string, pbj?: PbjChoice) => {
    if (!dialog || dialog.kind === "delete") return;
    setBusy(true);
    setError(null);
    try {
      if (dialog.kind === "create" && pbj?.kind === "pbj") {
        const created = await API.createPbjProject({ name, ...paketInput(pbj) });
        // The shared list carries the projects row (kind pbj); read it again.
        list.set(await API.listProjects());
        setDialog(null);
        navigate(projectPath(created.project.id));
      } else if (dialog.kind === "create") {
        // A Penyedia's project is a paket (the API default for the role).
        const project = await API.createProject(name);
        list.set([project, ...rows.filter((r) => r.id !== project.id)]);
        setDialog(null);
        navigate(projectPath(project.id));
      } else {
        const saved = await API.renameProject(dialog.project.id, name);
        list.set(rows.map((r) => (r.id === saved.id ? saved : r)));
        setDialog(null);
      }
    } catch (err) {
      setError(projectError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!dialog || dialog.kind !== "delete") return;
    const target = dialog.project;
    setBusy(true);
    setError(null);
    try {
      await API.deleteProject(target.id);
      list.set(rows.filter((r) => r.id !== target.id));
      setDialog(null);
      // A PBJ project goes back to the Project page (reference confirm-delete), anything else to the chat.
      if (currentId === target.id) navigate(nonPenyedia && target.kind === "pbj" ? PBJ_PROJECTS_PATH : target.kind === "paket" ? PENYEDIA_PROJECTS_PATH : "/");
    } catch (err) {
      setError(projectError(err));
    } finally {
      setBusy(false);
    }
  };

  const api = useMemo<ProjectsApi>(
    () => ({
      list,
      openCreate: () => open({ kind: "create" }),
      openRename: (p) => open({ kind: "rename", project: p }),
      openDelete: (p) => open({ kind: "delete", project: p }),
    }),
    // list is a fresh object each render; its data is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list.data, list.loading, list.error, open],
  );

  if (!enabled) return <>{children}</>;

  const nameOpen = dialog !== null && dialog.kind !== "delete";
  // A Penyedia's Buat project starts from the LPSE announcement (penyedia/PaketCreateDialog.tsx).
  const paketCreate = penyedia && dialog?.kind === "create";
  return (
    <ProjectsContext.Provider value={api}>
      {children}
      {penyedia ? (
        <PaketCreateDialog
          open={paketCreate}
          onClose={close}
          onProject={(project) => list.set([project, ...(list.data ?? []).filter((r) => r.id !== project.id)])}
          onOpen={(projectId) => {
            setDialog(null);
            navigate(projectPath(projectId));
          }}
        />
      ) : null}
      <ProjectNameDialog
        open={nameOpen && !paketCreate}
        mode={dialog?.kind === "rename" ? "rename" : "create"}
        initial={dialog?.kind === "rename" ? dialog.project.name : ""}
        busy={busy}
        error={nameOpen ? error : null}
        paket={penyedia}
        pbj={nonPenyedia}
        onSubmit={save}
        onClose={close}
      />
      <Dialog
        open={dialog?.kind === "delete"}
        title="Hapus project ini?"
        size="sm"
        busy={busy}
        onClose={close}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={close} disabled={busy}>
              Batal
            </button>
            <button type="button" className="btn btn-primary" data-tone="danger" onClick={remove} aria-busy={busy || undefined} disabled={busy}>
              Hapus
            </button>
          </>
        }
      >
        <div className="pj-confirm">
          <p className="dialog-text">
            Project <strong>{dialog?.kind === "delete" ? dialog.project.name : ""}</strong> akan dihapus permanen. Tindakan ini tidak dapat dibatalkan.
          </p>
          {dialog?.kind === "delete" && error ? (
            <Notice tone="danger" title="Project belum terhapus">
              <p>{error}</p>
            </Notice>
          ) : null}
        </div>
      </Dialog>
    </ProjectsContext.Provider>
  );
}

interface ProjectNameDialogProps {
  open: boolean;
  mode: "create" | "rename";
  initial: string;
  busy: boolean;
  error: string | null;
  /** A Penyedia: the project follows a paket and its name is optional (default PAKET_DEFAULT_NAME). */
  paket?: boolean;
  /** A Non-Penyedia: Buat project asks the kind (Paket PBJ or Folder) and the PBJ route. */
  pbj?: boolean;
  onSubmit: (name: string, pbj?: PbjChoice) => void;
  onClose: () => void;
}

/** The name of a Penyedia's project created without one. */
export const PAKET_DEFAULT_NAME = "Project baru";

/** Buat project and Ganti nama project: one field, one sentence, one action. */
function ProjectNameDialog({ open, mode, initial, busy, error, paket = false, pbj = false, onSubmit, onClose }: ProjectNameDialogProps) {
  const create = mode === "create";
  const id = useId();
  const formId = `${id}-form`;
  const fieldId = `${id}-name`;
  const [value, setValue] = useState(initial);
  const [choice, setChoice] = useState<PbjChoice>(DEFAULT_PBJ_CHOICE);
  // Every opening starts from the current name (empty for Buat project).
  useEffect(() => {
    if (open) {
      setValue(initial);
      setChoice(DEFAULT_PBJ_CHOICE);
    }
  }, [open, initial]);
  const withPbj = create && pbj;
  const submitName = (n: string) => onSubmit(n, withPbj ? choice : undefined);
  const optionalName = create && paket;
  const name = cleanName(value) || (optionalName ? PAKET_DEFAULT_NAME : "");
  const pbjInvalid = withPbj && choice.kind === "pbj" && tahunError(choice) !== null;
  const ready = name.length > 0 && (create || name !== initial) && !busy && !pbjInvalid;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    submitName(name);
  };

  // Enter saves from the field (the footer button sits outside the form's
  // box); an IME composition never does.
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter" || e.nativeEvent.isComposing) return;
    e.preventDefault();
    if (ready) submitName(name);
  };

  return (
    <Dialog
      open={open}
      title={create ? "Buat project" : "Ganti nama project"}
      size="sm"
      busy={busy}
      onClose={onClose}
      footer={
        <button type="submit" form={formId} className="btn btn-primary" disabled={!ready} aria-busy={busy || undefined}>
          {create ? "Buat project" : "Simpan"}
        </button>
      }
    >
      <form id={formId} className="pj-form" onSubmit={submit} noValidate>
        <Field id={fieldId} label="Nama project" error={error}>
          <div className="control-wrap pj-input">
            <Glyph name="folder" />
            <input
              id={fieldId}
              className="control-icon-start"
              data-autofocus=""
              autoComplete="off"
              placeholder={optionalName ? PAKET_DEFAULT_NAME : "mis. Pengadaan Laptop 2027"}
              value={value}
              maxLength={PROJECT_NAME_MAX}
              readOnly={busy}
              aria-describedby={`${fieldId}-hint`}
              aria-invalid={error ? true : undefined}
              onChange={(e) => setValue(e.currentTarget.value)}
              onKeyDown={onKey}
            />
          </div>
        </Field>
        {withPbj ? <PbjCreateFields value={choice} onChange={setChoice} disabled={busy} /> : null}
        {create && !(withPbj && choice.kind === "pbj") ? (
          <div className="pj-info">
            <Glyph name="infoCircle" size={16} />
            <p>
              {optionalName
                ? "Satu project untuk satu paket. Setelah dibuat, Pluto membantu mengisi info paket dari pengumuman LPSE, dokumen pemilihan, atau cerita singkat Anda."
                : "Project mengumpulkan chat dan dokumen pengadaan dalam satu tempat. Cocok untuk pekerjaan yang berjalan lama, atau sekadar supaya rapi."}
            </p>
          </div>
        ) : null}
      </form>
    </Dialog>
  );
}

/** The sidebar section: the heading, Project baru, then the projects. */
export function ProjectsSection({ currentId: shellId }: { currentId: string | null }) {
  const projects = useProjects();
  const { projectId: currentId, conversationId } = useOpenProject(shellId);
  const [open, , toggle] = useFlag(NAV_PROJECTS_KEY, true);
  const role = useMaybeUser()?.role;
  const path = useLocation().path;
  if (!projects) return null;
  const seeAllPath = !role ? null : isNonPenyedia(role) ? PBJ_PROJECTS_PATH : role === "penyedia" ? PENYEDIA_PROJECTS_PATH : null;
  const { list, openCreate, openRename, openDelete } = projects;
  // The 5 newest only (the menu Project lists them all, and so does Lihat semua); the open
  // project stays in the list even when older. Folded, the section still shows the open
  // project's row (and its chats), so it stays in reach.
  const all = list.data ?? [];
  const latest = all.slice(0, SIDEBAR_PROJECTS);
  const openOlder = all.find((p) => p.id === currentId && !latest.includes(p));
  const rows = open ? (openOlder ? [...latest, openOlder] : latest) : all.filter((p) => p.id === currentId);

  let body: ReactNode = null;
  if (list.loading && !list.data) {
    body = (
      <p className="cs-note" role="status">
        Memuat project
      </p>
    );
  } else if (list.error) {
    body = (
      <div className="cs-note">
        <span>Project belum termuat.</span>
        <button type="button" className="btn btn-secondary btn-sm" onClick={list.reload}>
          Coba lagi
        </button>
      </div>
    );
  } else if (!open && rows.length === 0) {
    body = null;
  } else if (rows.length > 0) {
    body = (
      <ul className="cs-rows" aria-label="Daftar project">
        {rows.map((p) => (
          <Fragment key={p.id}>
            <SideRow
              label={p.name}
              icon={p.kind === "paket" ? "box" : p.kind === "pbj" ? "building" : "folder"}
              href={projectPath(p.id)}
              current={p.id === currentId}
              menuLabel={`Tindakan untuk ${p.name}`}
              items={[
                { id: "rename", label: "Ganti nama", icon: "edit2", onSelect: () => openRename(p) },
                { id: "delete", label: "Hapus", icon: "trash", tone: "danger", onSelect: () => openDelete(p) },
              ]}
            />
            {p.id === currentId ? <ProjectSubRows project={p} conversationId={conversationId} /> : null}
          </Fragment>
        ))}
      </ul>
    );
  }

  return (
    <section className="cs-section" data-divided="" aria-labelledby="cs-projects-title">
      <SectionHead id="cs-projects-title" title="Project" open={open} onToggle={toggle} controls="cs-projects-list" />
      <button type="button" className="cs-item cs-project-new" aria-haspopup="dialog" onClick={openCreate}>
        <span className="cs-item-icon">
          <Glyph name="folderAdd" size={18} />
        </span>
        <span className="cs-item-label">Project baru</span>
      </button>
      <div id="cs-projects-list" className="cs-fold" data-folded={open ? undefined : ""}>
        {body}
        {seeAllPath ? (
          <a className="cs-item cs-project-all" href={seeAllPath} aria-current={path === seeAllPath ? "page" : undefined}>
            <span className="cs-item-icon">
              <Glyph name="element3" size={18} />
            </span>
            <span className="cs-item-label">Lihat semua</span>
          </a>
        ) : null}
      </div>
    </section>
  );
}

/** The open project's latest conversations, indented under its row; nothing while loading, empty or failed. */
function ProjectSubRows({ project, conversationId }: { project: Project; conversationId: string | null }) {
  const list = useProjectConversations(project.id);
  const rows = (list.data ?? []).slice(0, SIDEBAR_PROJECT_CHATS);
  if (rows.length === 0) return null;
  return (
    <li className="cs-subrows-item">
      <ul className="cs-subrows" aria-label={`Chat terbaru di ${project.name}`}>
        {rows.map((c) => (
          <li key={c.id}>
            <a className="cs-subrow" href={projectConversationPath(project.id, c.id)} aria-current={c.id === conversationId ? "page" : undefined} title={c.title}>
              <span className="cs-subrow-text">{c.title}</span>
            </a>
          </li>
        ))}
      </ul>
    </li>
  );
}
